// Step 7 Phase 3 M2 — Full SparkViewerAdapter (non-XR).
//
// Scope M2 (per STEP7_F4_PHASE3_PLAN.md §M2):
//   - init + render loop with setAnimationLoop
//   - loadSplatScene with PendingLoad + idempotent disposePending + timeout + race-guard (§3.8)
//   - removeSplatScene
//   - raycast via SplatMesh.raycast (§3.4)
//   - captureFrame RAF-tick + canvas.toBlob, no preserveDrawingBuffer (§3.5)
//     XR-mode contract present (rejects on xrSession/pendingXRRequest/xrTransitioning)
//     even though M2 does not implement XR — M3 enterXR will activate it.
//   - computeFitCamera via SplatMesh.getBoundingBox (§3.4)
//   - setBeforeRender + onFrame frameListeners (§3.6)
//   - context loss handlers (webglcontextlost/webglcontextrestored)
//   - dispose with proper ordering (§3.7)
//
// XR (enterXR/exitXR) lands in M3.
//
// fileType mapping (Codex iter 1 §B2):
//   SplatFileType enum is `PLY|SPZ|SPLAT|KSPLAT|PCSOGS|PCSOGSZIP|RAD`.
//   Explicit SPLAT/KSPLAT for legacy formats; undefined for ply/
//   compressed.ply/sog (auto-detect via magic bytes by Spark SplatLoader).

import { SparkRenderer, SplatFileType, SplatMesh } from '@sparkjsdev/spark';
import * as THREE from 'three';
import type { ModelFormat } from '../api/types.js';
import { AR_SESSION_INIT, VR_SESSION_INIT } from '../xr/types.js';
import type { CameraFit, CapturedFrame, ViewerAdapter } from './adapter.js';
import { fitCameraFromBox3 } from './auto-fit.js';
import type {
  InitOptions,
  LoadOptions,
  ViewerCapabilities,
  ViewerEventDetailMap,
} from './types.js';

// M3 capabilities — full Spark adapter incl. WebXR base capability.
// WebXR scope ridotto B-PURO: capability declared, NOT commercial-grade
// VR/AR (utente conferma: VR commerciale = build Unity separata).
const M3_CAPABILITIES: ViewerCapabilities = Object.freeze({
  dispose: true,
  removeSplatScene: true,
  raycast: true,
  enterXR: true,
  exitXR: true,
  onProgress: true,
  contextLossHandler: true,
  beforeRenderHook: true,
  frameCallbacks: true,
  captureFrame: true,
  cameraFit: true,
});

const DEFAULT_LOAD_TIMEOUT_MS = 60_000;

interface PendingLoad {
  seq: number;
  mesh: SplatMesh;
  timeoutId: ReturnType<typeof setTimeout> | null;
  // Codex iter 4 NEW1 fix — idempotency flag. All paths route via
  // disposePending(); helper checks `disposed` so repeated calls are no-op.
  disposed: boolean;
}

interface CaptureRequest {
  resolve: (frame: CapturedFrame) => void;
  reject: (err: Error) => void;
}

interface ContextLossHandler {
  event: string;
  handler: EventListener;
}

function mapFormatToSparkFileType(format?: ModelFormat): SplatFileType | undefined {
  if (format === undefined) return undefined;
  switch (format) {
    case 'splat':
      return SplatFileType.SPLAT;
    case 'ksplat':
      return SplatFileType.KSPLAT;
    case 'ply':
    case 'compressed.ply':
    case 'sog':
      // Auto-detect via magic bytes — Spark internal SplatLoader.
      return undefined;
  }
}

export interface SparkAdapterOptions {
  loadTimeoutMs?: number;
}

export class SparkViewerAdapter implements ViewerAdapter {
  readonly capabilities = M3_CAPABILITIES;
  readonly events = new EventTarget();

  private renderer: THREE.WebGLRenderer | null = null;
  private spark: SparkRenderer | null = null;
  private scene: THREE.Scene | null = null;
  private camera: THREE.PerspectiveCamera | null = null;
  private splatMesh: SplatMesh | null = null;
  private rafId: number | null = null;
  private beforeRenderCb: (() => void) | null = null;
  private frameListeners = new Set<(now: number) => void>();
  private captureRequest: CaptureRequest | null = null;
  private contextLossHandlers: ContextLossHandler[] = [];

  // XR fields — declared per plan §3.1 (M3 will populate via enterXR/exitXR).
  // captureFrame guard already checks them so M3 swap doesn't need to retro-fit.
  private xrSession: XRSession | null = null;
  private pendingXRRequest: AbortController | null = null;
  private xrTransitioning = false;

  // Async load lifecycle (plan §3.8)
  private currentLoadSeq = 0;
  private pendingLoad: PendingLoad | null = null;
  private disposed = false;
  private readonly loadTimeoutMs: number;

  constructor(opts: SparkAdapterOptions = {}) {
    this.loadTimeoutMs = opts.loadTimeoutMs ?? DEFAULT_LOAD_TIMEOUT_MS;
  }

  init = async (opts: InitOptions): Promise<void> => {
    const renderer = new THREE.WebGLRenderer({
      canvas: opts.canvas,
      antialias: false, // Spark docs: AA doesn't help splats + costs perf
    });
    renderer.setPixelRatio(opts.ignoreDevicePixelRatio === true ? 1 : window.devicePixelRatio);
    renderer.setSize(opts.canvas.clientWidth, opts.canvas.clientHeight, false);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(
      60,
      opts.canvas.clientWidth / Math.max(1, opts.canvas.clientHeight),
      0.01,
      1000,
    );

    // Plan §3 / Codex iter 1 Q9: WebGLRenderer FIRST, then SparkRenderer,
    // then scene.add(spark) BEFORE adding SplatMesh objects.
    const spark = new SparkRenderer({ renderer });
    scene.add(spark);

    this.renderer = renderer;
    this.spark = spark;
    this.scene = scene;
    this.camera = camera;

    this.attachContextLossHandlers(opts.canvas);
    this.startWindowRAF();
  };

  loadSplatScene = async (url: string, opts: LoadOptions = {}): Promise<void> => {
    if (this.scene === null || this.camera === null) {
      throw new Error('SparkViewerAdapter.loadSplatScene called before init()');
    }

    // Plan §3.8 + Codex iter 3 NEW2 + iter 4 NEW1: dispose IMMEDIATA della
    // mesh pending precedente via helper idempotente.
    if (this.pendingLoad !== null) {
      this.disposePending(this.pendingLoad);
      this.pendingLoad = null;
    }

    // Dispose splatMesh assegnata precedente (replace scene)
    if (this.splatMesh !== null) {
      this.scene.remove(this.splatMesh);
      this.splatMesh.dispose();
      this.splatMesh = null;
    }

    const seq = ++this.currentLoadSeq;
    const fileType = mapFormatToSparkFileType(opts.format);
    const meshOptions: ConstructorParameters<typeof SplatMesh>[0] = {
      url,
      raycastable: true,
      onProgress: (event: ProgressEvent) => {
        if (event.lengthComputable) {
          this.events.dispatchEvent(
            new CustomEvent<ViewerEventDetailMap['progress']>('progress', {
              detail: { loaded: event.loaded, total: event.total },
            }),
          );
        }
      },
    };
    if (fileType !== undefined) meshOptions.fileType = fileType;
    const mesh = new SplatMesh(meshOptions);

    const pending: PendingLoad = { seq, mesh, timeoutId: null, disposed: false };
    this.pendingLoad = pending;

    const initWithTimeout = Promise.race([
      mesh.initialized,
      new Promise<never>((_, reject) => {
        pending.timeoutId = setTimeout(
          () => reject(new Error(`SplatMesh.initialized timeout after ${this.loadTimeoutMs}ms`)),
          this.loadTimeoutMs,
        );
      }),
    ]);

    try {
      await initWithTimeout;
    } catch (err) {
      this.disposePending(pending);
      if (this.pendingLoad === pending) this.pendingLoad = null;
      throw err;
    }

    // Race-guard: durante await arrivato altro load() / dispose() / abandoned.
    if (seq !== this.currentLoadSeq || this.disposed || this.pendingLoad !== pending) {
      this.disposePending(pending);
      return;
    }

    // Success: clear timer, attach mesh.
    if (pending.timeoutId !== null) {
      clearTimeout(pending.timeoutId);
      pending.timeoutId = null;
    }
    this.pendingLoad = null;
    this.scene.add(mesh);
    this.splatMesh = mesh;
    this.events.dispatchEvent(
      new CustomEvent<ViewerEventDetailMap['loaded']>('loaded', { detail: { url } }),
    );
  };

  removeSplatScene = async (_sceneIndex: number): Promise<void> => {
    // Spark non ha multi-scene API come mkkellogg (un SplatMesh per adapter).
    // sceneIndex ignorato per backward compat con orchestrator chiamante.
    if (this.splatMesh === null || this.scene === null) return;
    this.scene.remove(this.splatMesh);
    this.splatMesh.dispose();
    this.splatMesh = null;
  };

  raycast = (screenX: number, screenY: number): THREE.Vector3 | null => {
    if (this.camera === null || this.splatMesh === null || this.renderer === null) return null;
    const canvas = this.renderer.domElement;
    const ndc = new THREE.Vector2(
      (screenX / canvas.clientWidth) * 2 - 1,
      -(screenY / canvas.clientHeight) * 2 + 1,
    );
    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(ndc, this.camera);
    const hits: THREE.Intersection[] = [];
    this.splatMesh.raycast(raycaster, hits);
    if (hits.length === 0) return null;
    const hit = hits[0];
    return hit ? hit.point.clone() : null;
  };

  captureFrame = (): Promise<CapturedFrame> => {
    // Plan §3.5 + Codex iter 4 NEW2 + iter 5 NEW1: rifiuta durante xrSession,
    // pendingXRRequest in-flight, xrTransitioning rollback. M2 sempre permette
    // (XR fields stay null/false); M3 enterXR attiva il guard.
    if (this.xrSession !== null || this.pendingXRRequest !== null || this.xrTransitioning) {
      return Promise.reject(
        new Error(
          'captureFrame not supported during XR session (active, starting, or rolling back)',
        ),
      );
    }
    return new Promise((resolve, reject) => {
      this.captureRequest = { resolve, reject };
    });
  };

  computeFitCamera = (): CameraFit | null => {
    if (this.camera === null || this.splatMesh === null) return null;
    // Plan §3.4 + Codex iter 1 Q8: Spark SplatMesh.getBoundingBox preferito
    // a THREE.Box3.setFromObject (splats non sono Three geometry standard).
    let box: THREE.Box3;
    try {
      box = this.splatMesh.getBoundingBox();
    } catch {
      return null;
    }
    if (box.isEmpty()) return null;
    return fitCameraFromBox3(box, this.camera.fov);
  };

  setBeforeRender = (cb: (() => void) | null): void => {
    this.beforeRenderCb = cb;
  };

  onFrame = (cb: (now: number) => void): (() => void) => {
    this.frameListeners.add(cb);
    return () => {
      this.frameListeners.delete(cb);
    };
  };

  // Plan §3.3 + Codex iter 2 §B6 + iter 3 NEW1 + iter 4 NEW2 + iter 5 NEW1:
  // transactional WebXR lifecycle. Scope ridotto B-PURO: minimal lifecycle
  // correctness (capability-only, NOT commercial-grade Quest/Vision Pro).
  enterXR = async (mode: 'vr' | 'ar'): Promise<void> => {
    if (this.xrSession !== null) return; // idempotent
    if (this.renderer === null || this.disposed) {
      throw new Error('SparkViewerAdapter not initialized');
    }
    if (typeof navigator === 'undefined' || navigator.xr === undefined) {
      throw new Error('WebXR not available in this browser');
    }
    // Codex iter 4 NEW2: cancel pending captureRequest PRIMA di setAnimationLoop
    // swap. Evita fulfill in XR mode (blob blank).
    this.rejectPendingCapture('captureFrame canceled: XR session starting');

    this.pendingXRRequest = new AbortController();
    const sessionMode: XRSessionMode = mode === 'vr' ? 'immersive-vr' : 'immersive-ar';
    const sessionInit = mode === 'vr' ? VR_SESSION_INIT : AR_SESSION_INIT;

    let session: XRSession;
    try {
      session = await navigator.xr.requestSession(sessionMode, sessionInit);
    } catch (err) {
      this.pendingXRRequest = null;
      throw err;
    }
    if (this.disposed || this.pendingXRRequest === null) {
      // Disposed o canceled durante requestSession — chiudere immediatamente.
      void session.end().catch(() => {});
      return;
    }
    this.pendingXRRequest = null;

    // Pre-assign listener + xrSession PRIMA di setSession (Codex iter 2 §B6).
    session.addEventListener('end', this.handleSessionEnd);
    this.xrSession = session;

    // ORDER MATTERS (Codex iter 3 NEW1, allineato a mkkellogg-adapter.ts:448-459):
    // Cancel window RAF, enable xr, setAnimationLoop FIRST, THEN setSession.
    // Reverse order would resume window RAF in parallel with XR RAF.
    if (this.rafId !== null) cancelAnimationFrame(this.rafId);
    this.rafId = null;
    this.renderer.xr.enabled = true;
    this.renderer.setAnimationLoop(this.renderTick);

    try {
      await this.renderer.xr.setSession(session);
    } catch (err) {
      // Codex iter 5 NEW1: xrTransitioning=true chiude finestra captureFrame
      // race durante rollback (xrSession=null + pendingXRRequest=null ma
      // renderer ancora in stato XR-transitioning).
      this.xrTransitioning = true;
      try {
        // Transactional rollback: detach listener + null xrSession FIRST,
        // session.end (lets WebXRManager run cleanup), setAnimationLoop(null) +
        // enabled=false (Three.js reacts to sessionend; disable AFTER), restart
        // standard RAF.
        session.removeEventListener('end', this.handleSessionEnd);
        this.xrSession = null;
        try {
          await session.end();
        } catch {
          /* best-effort */
        }
        try {
          this.renderer.setAnimationLoop(null);
        } catch {
          /* ignore */
        }
        this.renderer.xr.enabled = false;
        if (!this.disposed) this.startWindowRAF();
      } finally {
        this.xrTransitioning = false;
      }
      throw err;
    }

    this.events.dispatchEvent(
      new CustomEvent<ViewerEventDetailMap['xr-session-started']>('xr-session-started', {
        detail: { mode },
      }),
    );
  };

  exitXR = async (): Promise<void> => {
    if (this.xrSession === null) return;
    try {
      await this.xrSession.end();
    } catch {
      /* idempotent */
    }
  };

  private handleSessionEnd = (): void => {
    if (this.xrSession !== null) {
      this.xrSession.removeEventListener('end', this.handleSessionEnd);
      this.xrSession = null;
    }
    if (this.renderer !== null) {
      try {
        this.renderer.setAnimationLoop(null);
      } catch {
        /* ignore */
      }
      this.renderer.xr.enabled = false;
    }
    if (!this.disposed) this.startWindowRAF();
    this.events.dispatchEvent(
      new CustomEvent<ViewerEventDetailMap['xr-session-ended']>('xr-session-ended', {
        detail: {},
      }),
    );
  };

  private rejectPendingCapture(reason: string): void {
    if (this.captureRequest === null) return;
    const req = this.captureRequest;
    this.captureRequest = null;
    req.reject(new Error(reason));
  }

  getThreeScene = (): THREE.Scene => {
    if (this.scene === null) throw new Error('SparkViewerAdapter not initialized');
    return this.scene;
  };

  getCamera = (): THREE.PerspectiveCamera => {
    if (this.camera === null) throw new Error('SparkViewerAdapter not initialized');
    return this.camera;
  };

  getRenderer = (): THREE.WebGLRenderer => {
    if (this.renderer === null) throw new Error('SparkViewerAdapter not initialized');
    return this.renderer;
  };

  setCamera = (
    pos: readonly [number, number, number],
    lookAt: readonly [number, number, number],
    fov?: number,
  ): void => {
    if (this.camera === null) throw new Error('SparkViewerAdapter not initialized');
    this.camera.position.set(pos[0], pos[1], pos[2]);
    this.camera.lookAt(lookAt[0], lookAt[1], lookAt[2]);
    if (fov !== undefined) {
      this.camera.fov = fov;
      this.camera.updateProjectionMatrix();
    }
  };

  dispose = async (): Promise<void> => {
    // Plan §3.7 dispose ordering:
    //  1. mark disposed (race-guard for in-flight loadSplatScene + XR start)
    //  2. cancel pendingXRRequest if in-flight
    //  3. end active xrSession + reset renderer.xr state
    //  4. cancel RAF
    //  5. dispose pending load (idempotent helper)
    //  6. dispose splatMesh
    //  7. reject pending capture
    //  8. detach context loss listeners
    //  9. dispose Spark/Three resources
    this.disposed = true;
    // Cancel pending XR request (in-flight requestSession will end via
    // disposed-check after its await resolves).
    this.pendingXRRequest = null;
    // End active session if present (handleSessionEnd will fire and clean up).
    if (this.xrSession !== null) {
      try {
        await this.xrSession.end();
      } catch {
        /* best-effort */
      }
    }
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    if (this.pendingLoad !== null) {
      this.disposePending(this.pendingLoad);
      this.pendingLoad = null;
    }
    if (this.splatMesh !== null && this.scene !== null) {
      this.scene.remove(this.splatMesh);
      this.splatMesh.dispose();
      this.splatMesh = null;
    }
    // Reject any pending captureRequest so callers don't hang.
    this.rejectPendingCapture('captureFrame canceled: adapter disposed');
    this.detachContextLossHandlers();
    if (this.spark !== null && this.scene !== null) {
      this.scene.remove(this.spark);
    }
    this.beforeRenderCb = null;
    this.frameListeners.clear();
    if (this.renderer !== null) {
      this.renderer.dispose();
      this.renderer = null;
    }
    this.spark = null;
    this.scene = null;
    this.camera = null;
  };

  private disposePending(p: PendingLoad): void {
    // Codex iter 4 NEW1: idempotent — safe to call from any path
    // (second-load, catch reject, race-guard, dispose adapter, timeout fire).
    if (p.disposed) return;
    p.disposed = true;
    if (p.timeoutId !== null) {
      clearTimeout(p.timeoutId);
      p.timeoutId = null;
    }
    p.mesh.dispose();
  }

  private fulfillCaptureIfPending(): void {
    const req = this.captureRequest;
    if (req === null || this.renderer === null) return;
    this.captureRequest = null;
    this.renderer.domElement.toBlob((blob) => {
      if (blob === null) {
        req.reject(new Error('canvas.toBlob returned null'));
        return;
      }
      const url = URL.createObjectURL(blob);
      req.resolve({
        blob,
        revoke: () => URL.revokeObjectURL(url),
      });
    }, 'image/png');
  }

  private renderTick = (now: number): void => {
    if (this.renderer === null || this.scene === null || this.camera === null) return;
    if (this.beforeRenderCb !== null) this.beforeRenderCb();
    for (const cb of this.frameListeners) cb(now);
    this.renderer.render(this.scene, this.camera);
    // Plan §3.5: capture in same RAF tick AFTER render() so canvas pixels
    // are fresh (no need for preserveDrawingBuffer always-on).
    this.fulfillCaptureIfPending();
  };

  private startWindowRAF(): void {
    if (this.rafId !== null) cancelAnimationFrame(this.rafId);
    const tick = (now: number): void => {
      this.renderTick(now);
      this.rafId = requestAnimationFrame(tick);
    };
    this.rafId = requestAnimationFrame(tick);
  }

  private attachContextLossHandlers(canvas: HTMLCanvasElement): void {
    const lostHandler = (ev: Event): void => {
      ev.preventDefault();
      this.events.dispatchEvent(new CustomEvent('context-lost'));
    };
    const restoredHandler = (): void => {
      this.events.dispatchEvent(new CustomEvent('context-restored'));
    };
    canvas.addEventListener('webglcontextlost', lostHandler as EventListener, false);
    canvas.addEventListener('webglcontextrestored', restoredHandler as EventListener, false);
    this.contextLossHandlers.push(
      { event: 'webglcontextlost', handler: lostHandler as EventListener },
      { event: 'webglcontextrestored', handler: restoredHandler as EventListener },
    );
  }

  private detachContextLossHandlers(): void {
    if (this.renderer === null) return;
    const canvas = this.renderer.domElement;
    for (const { event, handler } of this.contextLossHandlers) {
      canvas.removeEventListener(event, handler);
    }
    this.contextLossHandlers = [];
  }
}
